import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import * as Sentry from '@sentry/vue';
import { useServices } from '../api/services';
import type { QuoteRow } from '../api/services/interfaces/QuoteService';
import type { ReportStatus } from './useInvoicesStore';
import type { Address } from './useAppointmentsStore';
import { useTenantStore } from './useTenantStore';

/**
 * Rich quote-detail domain shape — co-located with the store per
 * Phase 5.5 D-13/D-14. Mirrors the field-by-field projection of
 * `../api/app/views/v2/quotes/_quote.json.jbuilder`:
 *
 *   - id / job_id / customer_id              ← jbuilder line 4
 *   - quote_type (api_quote_type)            ← jbuilder line 6 — 'two_column' | other
 *   - status (api_quote_status, lowercased)  ← jbuilder line 7
 *   - name / summary (HTML, nullable)        ← jbuilder line 4
 *   - quote_date / sent_at (ISO 8601)        ← jbuilder lines 16-17
 *   - editable / archived                    ← jbuilder lines 19-20
 *   - display_membership_savings             ← jbuilder line 21
 *   - actual_/comparison_membership_program_*← jbuilder lines 9-12 (comparison only on two_column)
 *   - customer / billing_customer            ← jbuilder lines 23-24 (customer_short)
 *   - billing_address / service_address      ← jbuilder lines 27-30 (Address)
 *   - signature                              ← jbuilder lines 32-35 (kind/image_url/signature_time)
 *   - items_attributes[]                     ← jbuilder lines 37-43 (line items)
 *   - discount_attributes                    ← jbuilder lines 45-50 (id/code/kind/percent)
 *   - financials                             ← jbuilder lines 52-62 (subtotal/discount/late_fee/tax/total/savings cents)
 *   - links (HATEOAS)                        ← jbuilder line 75
 *   - payments_as_low_as_cents               ← jbuilder line 76
 *   - financing_options_active               ← jbuilder line 77
 *   - jobless_financing                      ← jbuilder line 78
 *
 * Wire-contract drift discipline (project memory `project_wire_contract_drift`):
 * each field is cross-checked against (a) the jbuilder partial, (b) the Rails
 * permit list / scope in `controllers/v2/customer_quotes_controller.rb`, and
 * (c) the legacy `consumer-portal/src/store.ts` quote slice. Drifted keys do
 * not throw — they produce NaN money, undefined dates, and blank pages with
 * 200 OK. The adapter at `adapters/quoteDetail.ts` is the single seam that
 * absorbs every disagreement.
 *
 * NOTE (D-14): the minimal `Quote` type still lives in `useAppointmentsStore`
 * for the appointment-detail surface; this `QuoteDetail` type is purpose-built
 * for the `/quotes/:id` view and does NOT replace it.
 */

/** Customer projection emitted via `v2/customers/_customer_short`. */
export interface QuoteCustomerShort {
  customer_id: number;
  type?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  accounting_key?: string;
  authentication_token?: string;
  cable_token?: string;
  membership_status?: string;
}

/** Single line item on the quote (`items_attributes[]`). */
export interface LineItem {
  id: number;
  item_id?: number;
  item_type?: string;
  item_category_name?: string;
  unit_name?: string;
  quantity: number;
  unit_price_cents: number;
  total_cents: number;
  description?: string;
  warranty?: string;
  /** Per-line comparison data — only emitted on two_column quotes. */
  comparison_price_cents?: number;
  comparison_total_cents?: number;
  // Grouping (05.6 / 260611-gfb, additive — flat rows never set these). The
  // quote adapter mirrors the invoice grouping shape but keeps the quote
  // domain's snake_case naming (the invoice domain uses camelCase). A
  // `type:'group'` row is a leader carrying the group's AGGREGATE prices
  // (`unit_price_cents`/`total_cents`) plus children under `items_in_group`;
  // every flat row leaves these unset.
  /** `'group'` for a group leader; `'lineItem'` (or absent) for a flat row. */
  type?: 'group' | 'lineItem';
  /** The group id (`group_id`) for a group leader; null/absent for flat rows. */
  group_id?: number | null;
  /** Children of a group leader, mapped through the flat adapter (no prices for customers). */
  items_in_group?: LineItem[];
  /** Optional HATEOAS links emitted on quote line items. */
  links?: Record<string, { href?: string } | undefined>;
}

/** Promo / discount block (`discount_attributes`). */
export interface Discount {
  id: number;
  code: string;
  kind: 'percentage' | 'amount' | string;
  percent?: number;
}

/** Financial totals block. All values are integer cents. */
export interface Financials {
  subtotal_cents: number;
  discount_amount_cents: number;
  late_fee_cents: number;
  sales_tax_cents: number;
  total_cents: number;
  /**
   * Two-column quotes always emit this; one-column only when savings exist.
   * Positive cents = "you saved" framing; negative cents (legacy) = "you could
   * have saved". Adapter preserves sign verbatim — view interprets.
   */
  savings_cents?: number;
}

/** Customer-signed acceptance block. */
export interface QuoteSignature {
  kind: string;
  image_url: string;
  signature_time: string;
}

/** HATEOAS link surface emitted on the quote detail wire. */
export interface QuoteDetailLinks {
  quote_proceed?: string;
  /**
   * Other server-minted links emitted by `quote_api_links` — kept open-shaped
   * so the adapter can pass through future entries without re-shaping.
   */
  [key: string]: { href?: string } | undefined | string;
}

/**
 * Single loan offer card returned by the financing endpoint. Used in
 * Plan 05.5-05; shape declared here so the wire-contract seam stays single.
 */
export interface LoanOffer {
  id: number;
  loan_program_id: number;
  description: string;
  down_payment_amount_cents: number;
  loan_amount_cents: number;
  interest_rate: number;
  term_months: number;
  payment_amount_cents: number;
  payment_summary: string;
  promotional_interest_rate?: number | null;
  promotional_term_months?: number | null;
  promotional_payment_amount_cents?: number | null;
  promotional_payment_summary?: string | null;
}

/** Group of offers from a single vendor (financing_options index payload). */
export interface VendorLoanOffers {
  lender: string;
  loan_offers: LoanOffer[];
}

/**
 * Rich quote-detail domain type (D-14). Field naming mirrors the jbuilder
 * snake_case verbatim — keeps the adapter purely a normalization seam
 * (omit/default/coerce) and avoids per-call-site key translation drift.
 */
export interface QuoteDetail {
  id: number;
  job_id: number;
  customer_id: number;
  quote_type: string;
  /** Lowercased: pending | denied | accepted | expired | draft. */
  status: string;
  name?: string;
  summary?: string;
  quote_date?: string;
  sent_at?: string;
  editable?: boolean;
  archived?: boolean;
  display_membership_savings?: boolean;
  /** Present on every quote that has an actual membership program assigned. */
  actual_membership_program_id?: number;
  actual_membership_program_name?: string;
  /** Present only when `quote.comparison?` is true (two-column quotes). */
  comparison_membership_program_id?: number;
  comparison_membership_program_name?: string;
  customer: QuoteCustomerShort;
  billing_customer: QuoteCustomerShort;
  billing_address: Address;
  service_address: Address;
  signature?: QuoteSignature;
  items_attributes: LineItem[];
  discount_attributes?: Discount;
  financials: Financials;
  payments_as_low_as_cents: number;
  financing_options_active: boolean;
  jobless_financing: boolean;
  /**
   * Server-minted POST target for the proceed action. Mirrors
   * quoteRow.proceedUrl (the list-view sibling — both ultimately source from
   * the Rails `proceed_url` top-level wire field). Optional: absent when the
   * quote is not in a proceedable state (expired / accepted) or when the
   * backend HATEOAS gate closes. View layer prefers this over
   * `links.quote_proceed.href` when both are present (see QuoteDetail.vue's
   * `proceedUrl` computed). Plan 15 (UAT Gap 2 round 3) — closes the wire-
   * contract drift seam where the detail adapter never extracted this field
   * while the list adapter (quoteRow.ts:111) had been extracting it since
   * Phase 5.4.
   */
  proceed_url?: string;
  /** TC-708: the server's eligibility flag; the portal never re-derives it. */
  customer_can_sign: boolean;
  links?: QuoteDetailLinks;
}

/**
 * Detail-surface lifecycle status. Mirrors the `ReportStatus` shape (`idle`
 * | `loading` | `success` | `error`) so consumers can reuse the same gate
 * idioms across the report and detail surfaces (Phase 5.5 D-13).
 */
export type DetailStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * Plan 05.5-05 — financing poll cadence constants.
 *
 * Three timing knobs the financing transport leans on. Named constants make
 * the intent self-documenting at every call site and keep magic numbers out
 * of the loop body. Each cites the closing Phase 5.5 CONTEXT decision so a
 * future maintainer can find the rationale without digging through commits.
 */

/**
 * Debounce window for `fetchFinancingOffers(downPaymentCents)`.
 *
 * Phase 5.5 D-03: legacy-parity 800ms collapses rapid down-payment input
 * changes into a single network call against the LAST input value. Bumping
 * the requestId immediately (before the debounce fires) is how prior
 * in-flight poll loops learn they are stale.
 */
const DEBOUNCE_MS = 800;

/**
 * Inter-poll delay between consecutive `fetchFinancingOptions` calls when
 * the server is still negotiating with Service Finance (`loading: true`).
 *
 * Phase 5.5 D-02: 1500ms — fast enough to feel responsive on the post-quote
 * page; slow enough that the 30-second cap allows ~20 attempts. Locked in
 * the wire-async contract (port research §3.3).
 */
const POLL_INTERVAL_MS = 1500;

/**
 * Hard cap on the polling loop. After this elapsed time the loop exits with
 * `financingStatus = 'error'` and emits a Sentry capture (tenant + quoteId
 * only — see `handleFinancingCapReached` for the PII boundary rationale).
 *
 * Phase 5.5 D-02: 30000ms — Service Finance typically responds within a few
 * seconds; 30s is the soft outage threshold beyond which the inline error +
 * Try again button is the better UX than continuing to spin.
 */
const POLL_CAP_MS = 30_000;

/**
 * Financing-surface lifecycle status (Plan 05.5-05 / D-02 + sketch-findings).
 *
 *   - 'idle'    — no fetch in flight; initial state and post-clearFinancing
 *   - 'loading' — debounced poll loop is running (panel renders skeleton)
 *   - 'ready'   — non-empty offers loaded (panel renders offer cards)
 *   - 'empty'   — wire returned `data: []` or all vendors had zero loan_offers
 *                 (panel renders the "no offers" copy per sketch-findings)
 *   - 'error'   — poll loop exceeded the 30000ms cap (panel renders inline
 *                 error + Try again button per D-02)
 *
 * The 'empty' / 'ready' split lets the panel pick copy without re-deriving
 * from `financingOffers.length` at every render site (single-source-of-truth
 * rule from Phase 4.6 D-43 — derive once at the boundary).
 */
export type FinancingStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

/**
 * Quote report store. Holds the full consumer quotes datatable response for
 * nav badges and the quote list. The Rails scope returns BOTH :pending and
 * :denied rows, so `pendingCount` filters on the adapted `status` (D-03).
 *
 * Plan 05.5-04 (D-13) adds the detail/proceed surface co-located with the
 * report surface: `currentQuote`, `currentStatus`, `currentError`,
 * `fetchQuote(id)`, `proceedQuote(proceedUrl, notes?)`, `clearCurrent()`.
 *
 * Per D-05 revised (05.4-REVIEW.md WR-06 resolution) "Proceed with service"
 * only pings the dispatcher — the quote stays pending server-side and is
 * still returned by the report. `proceedQuote` therefore does NOT call
 * `invalidate()`; the cached report survives so the proceeded card remains
 * in the list (legacy parity). This supersedes the original Plan 05.5-04
 * D-16 decision.
 *
 * Cross-store invalidation is intentionally NOT performed here either:
 * `proceedQuote` does NOT call `useInvoicesStore.invalidate()` because a
 * quote-proceed does not change the invoice surface.
 */
// eslint-disable-next-line max-lines-per-function -- defineStore setup aggregates the report surface (Plan 05.4-02), the pendingCount/badgeCount selectors (D-06), and the detail/proceed + financing surfaces (Plan 05.5-04 D-13, Plan 05.5-05) per the explicit co-located decision; splitting into separate composables would scatter related state and require duplicating mocks across stores.
export const useQuotesStore = defineStore('quotes', () => {
  const toast = useToast();

  // --- Report surface (unchanged from Plan 05.4-02; do not relocate) ---
  const report = ref<QuoteRow[]>([]);
  const status = ref<ReportStatus>('idle');
  const fetchedAt = ref<number | null>(null);
  const error = ref<unknown>(null);

  // D-03: report carries both pending and denied rows; only pending badges.
  const pendingCount = computed(
    () => report.value.filter((row) => row.status === 'pending').length,
  );

  // D-06 wins over D-03 at the badge surface: the /quotes list renders denied
  // rows as indistinguishable "Pending" cards, so the nav badge must count what
  // the list shows — ALL report rows, not pending-only. Intentionally distinct
  // from pendingCount (kept for any pending-specific consumer).
  const badgeCount = computed(() => report.value.length);

  // --- Accepted list (TC-708 D-17): kept apart so badgeCount stays open-only ---
  const acceptedReport = ref<QuoteRow[]>([]);
  const acceptedStatus = ref<ReportStatus>('idle');

  // --- Detail surface (Plan 05.5-04 D-13/D-14) ---
  const currentQuote = ref<QuoteDetail | null>(null);
  const currentStatus = ref<DetailStatus>('idle');
  const currentError = ref<unknown>(null);

  // --- Financing surface (Plan 05.5-05 D-01..D-04) ---
  //
  // The financing transport is a debounced (800ms) poll loop against
  // `quoteService.fetchFinancingOptions` that ticks every 1500ms until the
  // server flips `loading: false` OR 30000ms elapses (D-02). A monotonic
  // `financingRequestId` counter implements the stale-guard (D-03): any
  // in-flight poll whose captured id no longer matches drops its response
  // without mutating state — D-03 explicitly rejects request-cancellation
  // signal-based abort (no existing rebuild pattern to mirror), so the
  // requestId pattern is the single stale-response mechanism.
  const financingStatus = ref<FinancingStatus>('idle');
  const financingOffers = ref<VendorLoanOffers[]>([]);
  const financingError = ref<unknown>(null);
  // Closure-scoped — never exported; reset by `clearFinancing`.
  let financingRequestId = 0;
  let financingDebounceTimer: ReturnType<typeof globalThis.setTimeout> | null =
    null;

  async function fetchReport(): Promise<void> {
    status.value = 'loading';
    error.value = null;
    try {
      const { quotes } = useServices();
      // prettier-ignore
      const response = await quotes.fetchQuoteReport({ page: 0, rows: 200, sortField: 'id', sortOrder: -1 });
      report.value = response.data;
      status.value = 'success';
      fetchedAt.value = Date.now();
    } catch (err) {
      // Preserve last-known report so badges don't flicker on transient
      // failure. Sentry already captures via axios interceptor.
      status.value = 'error';
      error.value = err;
    }
  }

  /** TC-708 D-17 — signed quotes for the Accepted tab; keeps last rows on failure, like fetchReport. */
  async function fetchAcceptedReport(): Promise<void> {
    acceptedStatus.value = 'loading';
    try {
      const { quotes } = useServices();
      // prettier-ignore
      const response = await quotes.fetchQuoteReport({ page: 0, rows: 200, sortField: 'id', sortOrder: -1, quoteStatus: 'signed' });
      acceptedReport.value = response.data;
      acceptedStatus.value = 'success';
    } catch {
      acceptedStatus.value = 'error';
    }
  }

  /**
   * Plan 05.5-04 — fetch a single quote detail.
   *
   * WR-05 race-fix rule: callers MUST bind the awaited return value, NOT
   * re-read `store.currentQuote` after the await — a concurrent navigation
   * can mutate the singleton between resolve and read. The action assigns
   * the singleton AND returns the value so both surfaces work.
   *
   * On failure: sets `currentStatus = 'error'`, `currentError = err`, and
   * explicitly clears `currentQuote = null`. Unlike the report-cache rule
   * (which preserves last-known to keep badges stable), the detail view's
   * `v-else-if="!quote"` is the error branch, so clearing is correct.
   */
  async function fetchQuote(id: number): Promise<QuoteDetail> {
    currentStatus.value = 'loading';
    currentError.value = null;
    try {
      const { quotes } = useServices();
      const data = await quotes.getQuote(id);
      currentQuote.value = data;
      currentStatus.value = 'success';
      return data;
    } catch (err) {
      currentStatus.value = 'error';
      currentError.value = err;
      currentQuote.value = null;
      throw err;
    }
  }

  /**
   * Plan 05.5-09 — canonical Pendo presence-check helper.
   *
   * Every store-side Pendo call routes through this helper so the presence
   * check is single-sourced. The helper SWALLOWS any underlying error from
   * the Pendo SDK: analytics must never break the app (port research §1 +
   * `useAppointmentsStore.trackQuoteProceeded` precedent). Mirrors the same
   * presence-check pattern that lives in `usePendoQuoteEvents.ts` on the
   * view side — there are two copies because the store cannot import a
   * Vue composable, but the SHAPE is identical.
   */
  function pendoTrack(event: string, payload: Record<string, unknown>): void {
    if (
      typeof window === 'undefined' ||
      typeof window.pendo === 'undefined' ||
      typeof (window.pendo as { track?: unknown }).track !== 'function'
    ) {
      return;
    }
    try {
      window.pendo.track(event, payload);
    } catch {
      /* analytics never breaks the app */
    }
  }

  /**
   * Plan 05.5-09 — canonical Pendo events for the quote-accept flow.
   *
   * Replaces the legacy single `customer.quote.proceeded` event with the
   * pair `quote_accept_success` / `quote_accept_failed` from the CONTEXT
   * "Integration Points" table. Payload always carries `{ tenant, quote_id }`
   * at minimum; failure additionally carries `error_type` (the rejection's
   * constructor name — `'TypeError'`, `'AxiosError'`, etc.).
   */
  function trackQuoteAcceptSuccess(quoteId: number | undefined): void {
    const tenantStore = useTenantStore();
    pendoTrack('quote_accept_success', {
      tenant: tenantStore.config?.tenantName,
      quote_id: quoteId,
    });
  }

  function trackQuoteAcceptFailed(
    quoteId: number | undefined,
    err: unknown,
  ): void {
    const tenantStore = useTenantStore();
    const errorType = err instanceof Error ? err.constructor.name : typeof err;
    pendoTrack('quote_accept_failed', {
      tenant: tenantStore.config?.tenantName,
      quote_id: quoteId,
      error_type: errorType,
    });
  }

  /**
   * D-04 + Plan 05.5-04 — URL-driven proceed (port research §6 Blocker B fix).
   *
   * POSTs the caller-supplied `proceedUrl` verbatim via the service layer.
   * HATEOAS gate: throws BEFORE any HTTP call when `proceedUrl` is missing,
   * empty, null, or undefined (T-05.5-08).
   *
   * On success:
   *   1. Invalidate the OWN report (D-16) so the next `/quotes` visit
   *      refetches and the pending badge count is correct.
   *   2. Leave `currentQuote.status` alone: Proceed notifies the office and the
   *      quote stays pending server-side (TC-708 D-14).
   *   3. Fire Pendo `quote_accept_success` (Plan 05.5-09 — canonical event
   *      name from CONTEXT "Integration Points"; replaces the legacy
   *      `customer.quote.proceeded` event from Plan 05.5-04).
   *
   * On failure: toast a warn-severity message ("Quote" / "We couldn't submit
   * your request. Please try again." / life 8000), fire Pendo
   * `quote_accept_failed` with `{ tenant, quote_id, error_type }`, then rethrow
   * so the caller (modal) can re-enable the submit button. Sentry capture is
   * handled by the axios interceptor on the rethrown error — the store does
   * NOT call `captureException` explicitly here.
   */
  async function proceedQuote(
    proceedUrl: string,
    notes?: string,
  ): Promise<void> {
    if (!proceedUrl) {
      throw new Error(
        'Quote cannot be proceeded — HATEOAS gate is closed (no proceed URL).',
      );
    }
    const quoteIdForEvent = currentQuote.value?.id;
    try {
      const { quotes } = useServices();
      await quotes.proceedQuote(proceedUrl, notes ?? '');
      // D-05 revised: do NOT call invalidate() — the proceeded quote stays
      // pending server-side and is still returned by the report (legacy
      // parity, 05.4-REVIEW.md WR-06). Invalidating would clear the list
      // and force a refetch that returns the same quote, breaking the
      // list-view contract that the proceeded card REMAINS after close.
      trackQuoteAcceptSuccess(currentQuote.value?.id ?? quoteIdForEvent);
    } catch (err) {
      toast.add({
        severity: 'warn',
        summary: 'Quote',
        detail: "We couldn't submit your request. Please try again.",
        life: 8000,
      });
      trackQuoteAcceptFailed(quoteIdForEvent, err);
      throw err;
    }
  }

  /**
   * TC-708 — sign via the new API. The 200 payload replaces `currentQuote` when it is
   * the same quote (AC8, no refetch). Failures rethrow untouched: the dialog shows the 422 message.
   */
  async function signQuote(
    quoteId: number,
    signatureImage: string,
    notes?: string,
  ): Promise<QuoteDetail> {
    const { quotes } = useServices();
    const signed = await quotes.signQuote(quoteId, signatureImage, notes);
    if (currentQuote.value?.id === quoteId) currentQuote.value = signed;
    markSigned(quoteId);
    return signed;
  }

  /** TC-708 — a signed quote leaves the open list (and badge) and the Accepted tab refetches. */
  function markSigned(quoteId: number): void {
    removeQuote(quoteId);
    acceptedStatus.value = 'idle';
  }

  /**
   * TC-708 D-7 — fetch one quote without touching the detail-page singleton. Times out
   * because axios has none and a hung request would hold the appointment page's button.
   */
  async function peekQuote(
    id: number,
    timeoutMs = 10_000,
  ): Promise<QuoteDetail> {
    const { quotes } = useServices();
    let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = globalThis.setTimeout(
        () => reject(new Error('Quote fetch timed out')),
        timeoutMs,
      );
    });
    try {
      return await Promise.race([quotes.getQuote(id), timeout]);
    } finally {
      globalThis.clearTimeout(timer);
    }
  }

  /**
   * Plan 05.5-04 — route-leave reset for the detail surface.
   * Touches the detail refs only; the report cache (`report`, `status`,
   * `fetchedAt`, `error`, `pendingCount`) is left untouched so list-page
   * state survives a detail-page visit.
   *
   * Plan 05.5-05 — also calls `clearFinancing()` so route-leave drops any
   * in-flight financing poll loop (stale-guard via requestId bump).
   */
  function clearCurrent(): void {
    currentQuote.value = null;
    currentStatus.value = 'idle';
    currentError.value = null;
    clearFinancing();
  }

  /**
   * Plan 05.5-05 — financing poll runner.
   *
   * Inside the closure (NOT exported) so `financingRequestId` and the
   * `currentQuote` snapshot are captured at call-start. Each iteration:
   *   1. Bail if a newer call has bumped requestId (stale-guard, D-03)
   *   2. Call the service
   *   3. Bail again after the await (response may have arrived after a
   *      newer call superseded us — drop on the floor without mutating state)
   *   4. On `loading: false`, assign offers and exit; map empty data → 'empty'
   *   5. Otherwise `await delay(1500)` and retry until 30000ms elapsed
   * On cap-reached: `financingStatus = 'error'` AND emit a Sentry capture
   * with extras `{ tenant, quoteId }` ONLY — downPaymentCents is deliberately
   * excluded (financial PII excluded at the boundary, not relying solely on
   * the Sentry scrubber per Plan 05.5-09 / T-05.5-12).
   *
   * No request-cancellation signal: D-03 explicit rejection — no existing
   * rebuild pattern to mirror; requestId is sufficient and simpler.
   */
  /**
   * Apply a successful (`loading: false`) financing response to state.
   * Maps empty data (no vendors OR all vendors empty) to 'empty' so the
   * panel renders the "no offers available" copy off financingStatus alone
   * (Phase 4.6 D-43 single-source-of-truth — never re-derive from .length
   * at render sites).
   */
  function applyFinancingResponse(data: VendorLoanOffers[] | null): void {
    const offers = data ?? [];
    const hasOffers =
      offers.length > 0 &&
      offers.some((vendor) => (vendor.loan_offers?.length ?? 0) > 0);
    financingOffers.value = offers;
    financingStatus.value = hasOffers ? 'ready' : 'empty';
    financingError.value = null;
  }

  /**
   * Cap-reached handler — surface inline error (D-02) and emit Sentry
   * capture with tenant + quoteId ONLY. The action does NOT throw (soft
   * surface — panel absorbs the error per D-04 "rest of page interactive").
   * `downPaymentCents` is excluded at the boundary (T-05.5-12 financial
   * PII — not relying on the Sentry scrubber alone).
   */
  function handleFinancingCapReached(quoteId: number): void {
    financingStatus.value = 'error';
    financingError.value = new Error(
      `Financing poll exceeded ${POLL_CAP_MS}ms cap (D-02).`,
    );
    try {
      const tenantStore = useTenantStore();
      Sentry.captureException(financingError.value, {
        extra: {
          tenant: tenantStore.config?.tenantName,
          quoteId,
          // NOTE: `downPaymentCents` is intentionally OMITTED. Financial PII
          // is excluded at the boundary per Plan 05.5-09 / T-05.5-12; we do
          // not rely solely on the Sentry scrubber for `*_cents` keys.
        },
      });
    } catch {
      // Sentry being unavailable in non-app contexts (e.g., SSR previews)
      // must never propagate — the inline error state is the user-visible
      // contract.
    }
  }

  async function runFinancingPoll(
    myRequestId: number,
    customerId: number,
    quoteId: number,
    downPaymentCents: number,
  ): Promise<void> {
    const started = Date.now();
    while (Date.now() - started < POLL_CAP_MS) {
      if (myRequestId !== financingRequestId) return; // stale-guard pre-call
      try {
        const { quotes } = useServices();
        const res = await quotes.fetchFinancingOptions({
          customerId,
          quoteId,
          downPaymentCents,
        });
        if (myRequestId !== financingRequestId) return; // stale-guard post-call
        if (!res.loading) {
          applyFinancingResponse(res.data);
          return;
        }
      } catch (err) {
        // Mid-poll service rejection: drop state to error and stop. Stale
        // responses still drop silently via the requestId check above; this
        // path is reached only for the LATEST poll loop.
        if (myRequestId !== financingRequestId) return;
        financingStatus.value = 'error';
        financingError.value = err;
        return;
      }
      await new Promise<void>((resolve) => {
        globalThis.setTimeout(resolve, POLL_INTERVAL_MS);
      });
    }
    if (myRequestId !== financingRequestId) return;
    handleFinancingCapReached(quoteId);
  }

  /**
   * Plan 05.5-05 — debounced (800ms) financing offers fetch.
   *
   * Bumps `financingRequestId` immediately so any prior in-flight poll loop
   * drops on its next `requestId` check (stale-guard, D-03). Clears any
   * pending debounce timer; schedules a new one. The debounced callback
   * sets `financingStatus = 'loading'` and enters `runFinancingPoll`.
   *
   * `currentQuote` must be seeded (call `fetchQuote(id)` first) — the action
   * reads `currentQuote.value?.id` + `customer_id` to build the request
   * params. If `currentQuote` is null, the call is a no-op (the panel
   * should not render in that state per its feature-flag gate).
   */
  function fetchFinancingOffers(downPaymentCents: number): void {
    // Bump immediately so prior in-flight poll loops drop their responses
    // even before the new debounce window elapses.
    const myRequestId = ++financingRequestId;
    if (financingDebounceTimer !== null) {
      globalThis.clearTimeout(financingDebounceTimer);
      financingDebounceTimer = null;
    }
    financingDebounceTimer = globalThis.setTimeout(() => {
      financingDebounceTimer = null;
      // Re-check after debounce: another call may have superseded us during
      // the DEBOUNCE_MS window.
      if (myRequestId !== financingRequestId) return;
      const quote = currentQuote.value;
      if (!quote) return; // no-op when the detail isn't seeded
      financingStatus.value = 'loading';
      financingError.value = null;
      void runFinancingPoll(
        myRequestId,
        quote.customer_id,
        quote.id,
        downPaymentCents,
      );
    }, DEBOUNCE_MS);
  }

  /**
   * Plan 05.5-05 — financing-surface reset.
   *
   * Bumps `financingRequestId` so any in-flight poll loop drops its next
   * response, clears any pending debounce timer, and resets the three
   * state refs to initial. Called by `clearCurrent` on route-leave AND
   * by `reset()` on logout.
   */
  function clearFinancing(): void {
    // Bump the requestId so any awaiting poll loop's next stale-check fails
    // and it exits without mutating state.
    financingRequestId++;
    if (financingDebounceTimer !== null) {
      globalThis.clearTimeout(financingDebounceTimer);
      financingDebounceTimer = null;
    }
    financingStatus.value = 'idle';
    financingOffers.value = [];
    financingError.value = null;
  }

  // Drops a row by id. Signing uses it (markSigned); Proceed does not, since a
  // proceeded quote stays pending and the server still returns it (D-05 revised).
  function removeQuote(id: number): void {
    report.value = report.value.filter((r) => r.id !== id);
  }

  function invalidate(): void {
    report.value = [];
    status.value = 'idle';
    fetchedAt.value = null;
    error.value = null;
    acceptedReport.value = [];
    acceptedStatus.value = 'idle';
  }

  // quick-260527-njp: per-account clear on logout; same contract as invalidate.
  function reset(): void {
    invalidate();
    clearCurrent();
  }

  return {
    // Report surface (unchanged — byte-identical names from Plan 05.4-02).
    report,
    status,
    fetchedAt,
    error,
    pendingCount,
    badgeCount,
    fetchReport,
    proceedQuote,
    removeQuote,
    invalidate,
    reset,
    // Detail surface (Plan 05.5-04 D-13).
    currentQuote,
    currentStatus,
    currentError,
    fetchQuote,
    acceptedReport,
    acceptedStatus,
    fetchAcceptedReport,
    signQuote,
    markSigned,
    peekQuote,
    clearCurrent,
    // Financing surface (Plan 05.5-05 D-01..D-04).
    financingStatus,
    financingOffers,
    financingError,
    fetchFinancingOffers,
    clearFinancing,
  };
});
